/**
 * The organization code a mobile device needs in order to resolve this deployment.
 *
 * Each tenant is served from its own subdomain — `moh.deepecho.io`,
 * `elephant-healthcare.deepecho.io` — and the mobile app looks the deployment up by that
 * leading label. Local development is the exception: the app ships a debug override under
 * the literal code `local` that points at the host machine, so that is what we hand out
 * when the console is being served from a developer's machine.
 */

const LOCAL_ORG_CODE = 'local';

/** Hostnames that mean "a developer's machine", not a tenant. */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

export function orgCodeFromHostname(hostname: string): string {
  const host = (hostname || '').trim().toLowerCase().replace(/\.$/, '');

  if (!host || LOCAL_HOSTNAMES.has(host)) {
    return LOCAL_ORG_CODE;
  }

  // An IP address has no tenant label to read. Treat it as local rather than handing out
  // "192" as an org code, which would silently fail to resolve on the device.
  if (IPV4.test(host) || host.includes(':')) {
    return LOCAL_ORG_CODE;
  }

  const labels = host.split('.').filter(Boolean);

  // A bare apex like `deepecho.io` carries no tenant label either.
  if (labels.length < 3) {
    return LOCAL_ORG_CODE;
  }

  return labels[0];
}

/** Convenience wrapper for the running page. */
export function currentOrgCode(): string {
  return orgCodeFromHostname(
    typeof window === 'undefined' ? '' : window.location.hostname
  );
}

/**
 * What the enrollment QR encodes. Keys are explicit rather than positional so the mobile
 * side can parse it without depending on field order, and so extra fields can be added
 * later without breaking older apps.
 */
export function buildEnrollmentPayload(orgCode: string, licenseKey: string): string {
  return JSON.stringify({ orgCode, licenseKey });
}
